<template>
  <main
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <section class="card">
      <header class="page-header">
        <h1
          id="page-title"
          class="h1 mt-0"
        >
          Willkommen
        </h1>
        <div class="actions">
          <button
            type="button"
            class="btn btn-primary"
            @click="goToNewNotification"
          >
            Neue Meldung
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="goToNewHint"
          >
            Neuer Hinweis
          </button>
        </div>
      </header>

      <!-- Laden -->
      <div
        v-if="loading"
        class="skeleton"
        aria-busy="true"
        aria-live="polite"
      >
        <div class="skeleton-line" />
        <div class="skeleton-line" />
        <div class="skeleton-line" />
      </div>

      <!-- Fehler -->
      <p
        v-else-if="error"
        class="notice notice-danger"
        role="alert"
      >
        {{ error }}
      </p>

      <!-- Kein Inhalt -->
      <p
        v-else-if="children.length === 0"
        class="notice"
        role="status"
      >
        Wartungsarbeiten: Anwesenheiten können derzeit nicht angezeigt werden.
      </p>

      <!-- Liste der Kinder -->
      <ul
        v-else-if="isMaintenanceExempt"
        class="kid-list d-none"
        aria-live="polite"
      >
        <li
          v-for="kid in children"
          :key="kid.id"
          class="row"
        >
          <div class="name">
            {{ kid.name }}
          </div>
          <div
            class="status"
            :class="kid.present ? 'present' : 'absent'"
          >
            <span
              class="dot"
              :class="kid.present ? 'dot-success' : 'dot-danger'"
              aria-hidden="true"
            />
            <span class="label">{{ kid.present ? 'In der Schule' : 'Nicht in der Schule' }}</span>
          </div>
        </li>
      </ul>
      <p
        v-else
        class="notice mt-12"
        role="status"
      >
        Wartungsarbeiten: Diese Seite ist vorübergehend nicht voll verfügbar.
      </p>
    </section>
  </main>
</template>

<script setup>
import {ref, onMounted, computed} from 'vue'
import {useRouter} from 'vue-router'
import api from '@/services/api'
import {useAuthStore} from '@/stores/auth'
import { schoolSettingsApi } from '@/services/api/schoolSettings'

const loading = ref(true)
const error = ref('')
const children = ref([])
const router = useRouter()
const auth = useAuthStore()

const settingsLoading = ref(true)
const parentMaintenanceEnabled = ref(false)
const parentMaintenanceExemptUserIds = ref([])

const currentUserId = computed(() => auth.user?.id ?? auth.user?.userId ?? auth.user?.user_id ?? null)

const isMaintenanceExempt = computed(() => {
  // Wenn der Wartungsmodus nicht aktiv ist, gelten alle als befreit
  if (!parentMaintenanceEnabled.value) return true
  const uid = Number(currentUserId.value)
  return parentMaintenanceExemptUserIds.value.map((v) => Number(v)).includes(uid)
})

const canViewChildren = computed(() => {
  if (!parentMaintenanceEnabled.value) return true
  return isMaintenanceExempt.value
})

function goToNewNotification() {
  router.push({name: 'Notifications', query: {new: '1'}})
}

function goToNewHint() {
  router.push({name: 'TeacherInfos', query: {new: '1'}})
}

async function loadSettings() {
  settingsLoading.value = true
  try {
    const res = await schoolSettingsApi.getCurrent()
    const data = res.data || {}
    parentMaintenanceEnabled.value = Boolean(
       data.parentFeaturesMaintenanceEnabled ?? false
    )
    const exempt = data.parentFeaturesMaintenanceExemptUserIds      ?? []
    parentMaintenanceExemptUserIds.value = Array.isArray(exempt) ? exempt : []
  } catch (e) {
    parentMaintenanceEnabled.value = false
    parentMaintenanceExemptUserIds.value = []
  } finally {
    settingsLoading.value = false
  }
}

async function loadStatus() {
  loading.value = true
  error.value = ''
  try {
    const {data} = await api.get('/parent/children-status')
    const list = Array.isArray(data) ? data : (data?.children || [])

    children.value = list.map((k) => {
      const presentNormalized =
          typeof k.present === 'boolean'
              ? k.present
              : (k.inSchool ?? (String(k.status ?? '').toLowerCase() === 'present'))

      return {
        id: k.id ?? k.childId ?? k.uuid ?? k.name ?? Math.random().toString(36).slice(2),
        name: k.name ?? k.fullName ?? 'Kind',
        present: !!presentNormalized,
      }
    })
  } catch (e) {
    const msg =
        (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
        e?.message ||
        'Konnte Anwesenheitsstatus nicht laden.'
    error.value = msg
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  await loadSettings()
  if (canViewChildren.value) {
    loadStatus()
  } else {
    loading.value = false
  }
})
</script>

<style scoped>
.page {
  padding: 16px;
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 1px solid var(--border);
  padding-bottom: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  /* NEU: darf umbrechen */
  flex-wrap: wrap;
}

/* optional, falls global nicht vorhanden */
.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 500;
  margin: 0;

  /* NEU: darf schrumpfen, damit Buttons daneben Platz haben */
  min-width: 0;
}

/* NEU: Button-Gruppe sauber nebeneinander */
.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/* Liste/Zeilen */
.kid-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 8px;
}

.row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.name {
  font-weight: 500;
}

/* Statusanzeige */
.status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 20px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.dot-success {
  background: var(--success);
}

.dot-danger {
  background: var(--danger);
}

.present .label {
  color: var(--success);
}

.absent .label {
  color: var(--danger);
}

/* Skeleton Loader */
.skeleton {
  display: grid;
  gap: 8px;
}

.skeleton-line {
  height: 18px;
  border-radius: 6px;
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.06), rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.06));
  animation: pulse 1.6s infinite ease-in-out;
}

@media (max-width: 460px) {
  /* H1 nimmt volle Breite → Actions rutschen darunter */
  .h1 {
    flex: 1 0 100%;
  }

  .actions {
    width: 100%;
    justify-content: flex-start; /* optional: linksbündig auf Mobile */
  }
}

/* Utilities */
.mt-12 {
  margin-top: 12px;
}
</style>